/* Stock, held a batch at a time.
 *
 * A warehouse does not hold "40 cases of honey cake". It holds 24 cases of
 * batch L-2609 expiring in March and 16 of L-2704 expiring in June, and the
 * difference is the job: what may still be sold, what must move first, and what
 * has to be reordered. So a row here is a *lot* — one article, one batch, one
 * expiry — and an article held in three batches is three rows.
 */

import { api } from "./api";

export type StockRow = {
  id: number;
  product_id: number;
  name: string;
  sku: string;
  ean: string;
  image_url: string;
  thumb_url: string;
  unit: string;
  net_weight_kg: string | null;
  supplier_name: string;
  /** «Недоступна у поставщика» on this card (spec 20260915 §2.2). */
  supplier_gap_reason?: string;
  batch_no: string;
  manufactured_on: string | null;
  expires_on: string | null;
  /** Shelf life left. Negative for stock already out of date, null for a lot
   *  that carries no expiry — a blank column is the honest answer there. */
  days_left: number | null;
  /** The lot's shelf-life discount today, % off the base price. */
  expiry_discount_pct?: string | number | null;
  /** «Распродажа» in force today, and how many periods the lot carries. */
  sale_price_eur?: string | number | null;
  sale_reason?: string;
  sale_ends_on?: string | null;
  sale_periods?: number;
  /** «Место на складе» — the cell address, «A-02-05-3-01». */
  location?: string;
  quantity: string;
  /** What open orders hold of this lot, and what is left of it for a new
   *  order (2026-09-28). */
  reserved?: string | number | null;
  available?: string | number | null;
  /** The article's total across all its batches — what is counted per EAN. */
  on_hand_total?: string | null;
  /** The article is in the archive (greyed out, listed only in the archive view). */
  is_archived?: boolean;
  on_order: string;
  min_stock_qty: number | null;
  below_minimum: boolean;
  /** Null when nothing has shipped in the window: an article with no history
   *  has no rate to divide by, and a large number produced by a zero
   *  denominator is worse than a blank. */
  days_of_cover: number | null;
  /** «Статус наличия» of the article (2026-09-16), repeated across its lots. */
  availability_status?: "" | "in_stock" | "on_order" | "unavailable" | "expected";
  availability_expected_on?: string | null;
  /** What the goods are priced at. `price_eur` is the card's base sale price —
   *  the shelf's own figure — and the two cost figures are null for a reader
   *  without `purchase_prices.view`, so a page can drop those columns rather
   *  than print a shelf of zeroes. `vat_rate` is the card's, for the surfaces
   *  set to show prices with VAT. */
  price_eur?: number | string;
  vat_rate?: number | string;
  cost_price_eur?: number | string | null;
  landed_cost_eur?: number | string | null;
  is_orderable?: boolean;
  has_deposit?: boolean;
  sugar_excise?: boolean;
  is_alcohol?: boolean;
  is_coffee?: boolean;
};

/** Accounting marks a delivery note can record, in the sheet's order.
 *  «sugar» is the Latvian sugar-drinks excise — offered to LV companies only. */
export const RECEIPT_MARKS = ["deposit", "sugar", "alcohol", "coffee"] as const;
export type ReceiptMark = (typeof RECEIPT_MARKS)[number];

export type StockPage = {
  results: StockRow[];
  total: number;
  consumption_window_days: number;
};

export type ReplenishLine = {
  product_id: number;
  name: string;
  supplier_slug: string;
  supplier_name: string;
  on_hand: string;
  min_stock_qty: number;
  suggested_qty: string;
};

export type ReplenishDraft = {
  code: string;
  reference: string;
  supplier_name: string;
  lines: number;
};

export async function fetchStock(
  slug: string,
  params: {
    q?: string;
    supplier?: string;
    below?: boolean;
    page?: number;
    /** A Stock page column key; the whole shelf is sorted, not the page. */
    sort?: string;
    direction?: "asc" | "desc";
    /** The archive instead of the shelf. */
    archived?: boolean;
    /** The shelf with the archived articles among it. */
    with_archive?: boolean;
    /** A category takes its subcategories with it. */
    category_id?: number | null;
    /** Only articles carrying this accounting mark. */
    /** A built-in mark, or `c<id>` for one the company added itself. */
    mark?: string;
  } = {},
): Promise<StockPage> {
  const qs = new URLSearchParams();
  if (params.archived) qs.set("archived", "true");
  if (params.with_archive) qs.set("with_archive", "true");
  if (params.category_id) qs.set("category_id", String(params.category_id));
  if (params.q) qs.set("q", params.q);
  if (params.supplier) qs.set("supplier", params.supplier);
  if (params.below) qs.set("below", "true");
  if (params.mark) qs.set("mark", params.mark);
  qs.set("page", String(params.page ?? 1));
  if (params.sort) {
    qs.set("sort", params.sort);
    qs.set("direction", params.direction ?? "asc");
  }
  return (await api.get(`/t/${slug}/stock?${qs}`)) as StockPage;
}

/** Correct a lot: its batch, its dates, what is left of it. */
/** «Распродажа» on one lot (2026-09-28): why, and its periods. */
export type LotSalePeriod = { starts_on: string; ends_on: string | null; price_eur: string | number };
export type LotSale = {
  reason: "expiry" | "quantity";
  periods: LotSalePeriod[];
  /** Read-only: the card's base price, net EUR. */
  base_price_eur?: string | number | null;
};

export async function fetchLotSale(slug: string, lotId: number): Promise<LotSale> {
  return (await api.get(`/t/${slug}/stock/lots/${lotId}/sale`)) as LotSale;
}

/** Replaces the lot's periods; an empty list takes it off sale. */
export async function saveLotSale(slug: string, lotId: number, sale: LotSale): Promise<StockRow> {
  return (await api.put(`/t/${slug}/stock/lots/${lotId}/sale`, sale)) as StockRow;
}

export async function updateLot(
  slug: string,
  lotId: number,
  patch: Partial<Pick<StockRow, "batch_no" | "manufactured_on" | "expires_on" | "quantity" | "location">> & {
    /** Why a count went down — the alcohol report declares the first two. */
    writeoff?: WriteoffKind;
  },
): Promise<StockRow> {
  return (await api.patch(`/t/${slug}/stock/lots/${lotId}`, patch)) as StockRow;
}

export async function deleteLot(slug: string, lotId: number): Promise<void> {
  await api.delete(`/t/${slug}/stock/lots/${lotId}`);
}

/** «Минимальное количество остатка» for one article. Null clears it — the
 *  article stops being watched, which is different from a minimum of zero. */
export async function setMinimum(
  slug: string,
  productId: number,
  min: number | null,
): Promise<void> {
  await api.patch(`/t/${slug}/stock/products/${productId}/minimum`, { min_stock_qty: min });
}

/** «Статус наличия» for one article; an empty status clears it. */
export async function setAvailability(
  slug: string,
  productId: number,
  status: string,
  expectedOn: string | null,
): Promise<void> {
  await api.patch(`/t/${slug}/stock/products/${productId}/availability`, {
    availability_status: status,
    availability_expected_on: expectedOn || null,
  });
}

/* «Маркировка склада» (owner, 2026-09-24: «у кого-то только ряд-место»).
   A cell address is made of some of these parts, always in this order — from
   the general to the particular. The company ticks its own in Настройки →
   Склад; the address builder (LocationInput) offers only those. */
export type LocationSegment = "zone" | "row" | "rack" | "tier" | "cell";
export const LOCATION_SEGMENTS: LocationSegment[] = ["zone", "row", "rack", "tier", "cell"];

/* How each part prints on a label. A row, a rack and a cell carry two digits
   (01, 02 …); a tier one — it is counted from the floor up and rarely passes
   9; a zone is a letter. */
export const LOCATION_SEGMENT_SPEC: Record<LocationSegment, { width: number; example: string; labelKey: string }> = {
  zone: { width: 0, example: "A", labelKey: "stock.location.zone" },
  row: { width: 2, example: "02", labelKey: "stock.location.row" },
  rack: { width: 2, example: "05", labelKey: "stock.location.rack" },
  tier: { width: 1, example: "3", labelKey: "stock.location.tier" },
  cell: { width: 2, example: "01", labelKey: "stock.location.cell" },
};

/** One part as the label prints it: numbers padded, letters in upper case. */
export function padLocationPart(segment: LocationSegment, raw: string): string {
  const v = raw.trim();
  const width = LOCATION_SEGMENT_SPEC[segment].width;
  return width && /^\d+$/.test(v) ? v.padStart(width, "0") : v.toUpperCase();
}

/** The example address of a format — «A-02-05-3-01» for all five parts. */
export function exampleLocation(format: LocationSegment[]): string {
  return format.map((s) => LOCATION_SEGMENT_SPEC[s].example).join("-");
}

/** «Сборка товара»: the printed assembly sheet (default) and/or loading the
 *  order into a warehouse terminal — the second is recorded, not built yet. */
export const ASSEMBLY_METHODS = ["sheet", "terminal"] as const;
export type AssemblyMethod = (typeof ASSEMBLY_METHODS)[number];

/** «Скидки по сроку годности» (2026-09-28): a step is met by a lot with at
 *  most `threshold` days left, or at most `threshold` % of its shelf life. */
export type ExpiryStep = { mode: "days" | "percent"; threshold: number; discount: number };
export type ExpiryLadders = { company: ExpiryStep[]; categories: Record<string, ExpiryStep[]> };

export type StockSettings = {
  consumption_window_days: number;
  location_format: LocationSegment[];
  assembly_methods: AssemblyMethod[];
  expiry_discounts: ExpiryLadders;
  /** «Учётные признаки» the company keeps — they add their columns to the
   *  Products screen settings. */
  product_marks: ReceiptMark[];
  /** «Акцизный товар → Алкоголь»: the alcohol types the company trades in. */
  alcohol_types: string[];
  /** «Лицензия и склад»: the two numbers are the document profile's fields
   *  (Настройки → Документы); the address is the stock settings' own. */
  excise_licence_no: string;
  excise_warehouse_no: string;
  excise_warehouse_address: string;
  /** «Кофе» / «Сахаросодержащие напитки (LV)»: the company's excise rates. */
  excise_rates: ExciseRates;
};

/** Rates are EUR per 100 units — litres, or kilograms of coffee; "" = not
 *  set. The last sugar band is open-ended (`max_g` null). */
export type SugarTier = { max_g: string | null; rate: string };
export type ExciseRates = {
  coffee: { unit: "l" | "kg"; rate: string };
  sugar: { tiers: SugarTier[] };
};

/** Why a lot's count went down on Склад (2026-09-29): breakage, a move to
 *  the retail network, a tasting, or a counting error that moved nothing. */
export const WRITEOFF_KINDS = ["loss", "transfer", "tasting", "correction"] as const;
export type WriteoffKind = (typeof WRITEOFF_KINDS)[number];

/** «Виды алкоголя» (2026-09-29): the VID declaration categories («short») and
 *  the EU 2019/787 spirit-drink categories («full»); `vid` = the category the
 *  type is declared under. Names are official terms, not interface keys. */
export type AlcoholType = {
  code: string;
  list: "short" | "full";
  vid: string;
  names: { lv: string; en: string; ru: string; pl?: string };
};

let alcoholTypes: Promise<AlcoholType[]> | null = null;

/** The registry never changes while a page is open: read once. */
export function fetchAlcoholTypes(): Promise<AlcoholType[]> {
  alcoholTypes ??= (api.get("/meta/alcohol-types") as Promise<AlcoholType[]>).catch((e) => {
    alcoholTypes = null;
    throw e;
  });
  return alcoholTypes;
}

/** A type's name in the interface language, with English as the fallback. */
export function alcoholTypeName(t: AlcoholType | undefined, locale: string | null | undefined): string {
  if (!t) return "";
  const lang = (locale ?? "en").slice(0, 2);
  return lang === "lv" || lang === "ru" || lang === "pl" ? t.names[lang] || t.names.en : t.names.en;
}

/** One row of «Поставщики акцизных марок». */
export type StampSupplier = {
  id: number | null;
  name: string;
  reg_number: string;
  address: string;
  /** Read-only: cards that point at the row. */
  used?: number;
};

export async function fetchStampSuppliers(slug: string): Promise<StampSupplier[]> {
  return (await api.get(`/t/${slug}/stock/stamp-suppliers`)) as StampSupplier[];
}

export async function saveStampSuppliers(slug: string, rows: StampSupplier[]): Promise<StampSupplier[]> {
  return (await api.put(`/t/${slug}/stock/stamp-suppliers`, rows)) as StampSupplier[];
}

/** One row of Настройки → Склад → Паллеты. */
export type CompanyPallet = {
  id: number | null;
  code?: string;
  name: string;
  width_mm: number | null;
  length_mm: number | null;
  weight_kg: number | string | null;
  /** The platform's size: only the tick and the weight are the company's. */
  standard: boolean;
  enabled: boolean;
};

export async function fetchCompanyPallets(slug: string): Promise<CompanyPallet[]> {
  return (await api.get(`/t/${slug}/stock/pallets`)) as CompanyPallet[];
}

export async function saveCompanyPallets(slug: string, rows: CompanyPallet[]): Promise<CompanyPallet[]> {
  return (await api.put(`/t/${slug}/stock/pallets`, rows)) as CompanyPallet[];
}

export async function fetchStockSettings(slug: string): Promise<StockSettings> {
  return (await api.get(`/t/${slug}/stock/settings`)) as StockSettings;
}

/** Saves only what is passed: the Stock page sends the window, Settings the
 *  format, and neither resets the other. */
export async function saveStockSettings(
  slug: string,
  patch: Partial<StockSettings>,
): Promise<StockSettings> {
  const saved = (await api.put(`/t/${slug}/stock/settings`, patch)) as StockSettings;
  locationFormats.set(slug, Promise.resolve(saved.location_format));
  return saved;
}

/* The format is read once per company per page load: every row of the stock
   table has an address box, and forty requests for one answer would be
   silly. A member who may not read the stock settings gets every part — the
   builder they had before the format was a choice. */
const locationFormats = new Map<string, Promise<LocationSegment[]>>();

export function locationFormatFor(slug: string): Promise<LocationSegment[]> {
  let format = locationFormats.get(slug);
  if (!format) {
    format = fetchStockSettings(slug)
      .then((s) => (s.location_format?.length ? s.location_format : [...LOCATION_SEGMENTS]))
      .catch(() => [...LOCATION_SEGMENTS]);
    locationFormats.set(slug, format);
  }
  return format;
}

export async function fetchShortfalls(slug: string): Promise<{ lines: ReplenishLine[] }> {
  return (await api.get(`/t/${slug}/stock/replenish`)) as { lines: ReplenishLine[] };
}

/** Write the shortfall down as draft orders, one per supplier. Nothing is
 *  sent: a minimum is the company's own housekeeping rule, an order is a
 *  commitment to somebody else, and the gap is where a wrong minimum is
 *  caught. */
export async function draftReplenishment(
  slug: string,
  supplier: string,
): Promise<{ drafts: ReplenishDraft[] }> {
  const qs = new URLSearchParams({ supplier });
  return (await api.post(`/t/${slug}/stock/replenish/draft?${qs}`, {})) as { drafts: ReplenishDraft[] };
}

/** «Поместить в архив» — only an article with nothing left on the shelf — or bring it back. */
export async function archiveProduct(slug: string, productId: number, archived: boolean): Promise<void> {
  await api.put(`/t/${slug}/stock/products/${productId}/archive`, { archived });
}

/** The suppliers the shelf can be narrowed to — the cards' «Поставщик». */
export async function fetchStockSuppliers(slug: string): Promise<string[]> {
  return (await api.get(`/t/${slug}/stock/suppliers`)) as string[];
}

/** Re-check the whole minimum-stock position for a draft's supplier and
 *  rewrite its lines against today's shelf. */
export async function refreshReplenishment(slug: string, code: string): Promise<void> {
  await api.post(`/t/${slug}/stock/replenish/refresh/${code}`, {});
}

/** This company's own lots of one article, oldest expiry first — what the
 *  sender picks from when saying which batch is going on the lorry. */
export async function fetchLotsOfProduct(slug: string, productId: number): Promise<StockRow[]> {
  return (await api.get(`/t/${slug}/stock/lots?product_id=${productId}`)) as StockRow[];
}

/* ── The two ledgers: Inbound (what came in) and Outbound (what we invoiced) ──
 * Inbound is every delivery that reached the shelf — the receipts the
 * pipeline wrote when a platform order was confirmed received, and the
 * delivery notes typed in by hand from suppliers who are not on the platform.
 * Outbound is every invoice and credit note this company issued. Both read
 * the same way, and both turn into a file for the accountant. */

export type LedgerDirection = "inbound" | "outbound";
export type LedgerView = "documents" | "lines" | "turnover";

export type LedgerRow = {
  accounting_state?: string;
  id: number;
  /** "order" | "manual" on Inbound; "INV" | "CN" on Outbound. */
  kind: string;
  date: string;
  number: string;
  counterparty: string;
  counterparty_slug: string;
  currency: string;
  net: string;
  vat: string;
  gross: string;
  order_code: string;
  order_reference: string;
  document_id: number | null;
  can_delete: boolean;
  lines: number;
  /** Inbound only: the payment date typed off the note, or that it was prepaid. */
  payment_due_on?: string | null;
  prepaid?: boolean;
  entered_on?: string | null;
  /** Inbound only: bought across a border, no VAT. */
  is_export?: boolean;
  /** Inbound only: the accounting marks the note records. */
  marks?: ReceiptMark[];
};

/** A line's accounting marks — sent and read back for the marks the note ticks. */
export type LineMarks = {
  has_deposit?: boolean;
  sugar_excise?: boolean;
  is_alcohol?: boolean;
  alcohol_pct?: string | null;
  excise_stamp_range?: string;
  excise_stamp_supplier?: string;
  /** Litres per unit. */
  net_volume_l?: string | null;
  is_coffee?: boolean;
  /** Kilograms per unit. */
  net_weight_kg?: string | null;
};

export type LedgerLine = {
  id: number;
  document_id: number;
  document_number: string;
  document_date: string;
  counterparty: string;
  product_id: number | null;
  name: string;
  sku: string;
  ean: string;
  unit: string;
  quantity: string;
  unit_price?: string;
  vat_rate?: string;
  line_total: string;
  batch_no: string;
  manufactured_on: string | null;
  expires_on: string | null;
  location?: string;
} & LineMarks;

export type LedgerTurnover = {
  counterparty: string;
  counterparty_slug: string;
  documents: number;
  net: string;
  vat: string;
  gross: string;
};

export type LedgerPage = {
  rows: LedgerRow[];
  lines: LedgerLine[];
  turnover: LedgerTurnover[];
  total: number;
  sum_net: string;
  sum_vat: string;
  sum_gross: string;
};

export type LedgerQuery = {
  date_from?: string;
  date_to?: string;
  q?: string;
  /** Supplier name on Inbound, buyer name on Outbound. */
  counterparty?: string;
  /** Outbound only: INV | CN. */
  kind?: string;
  view?: LedgerView;
  page?: number;
};

function ledgerQs(direction: LedgerDirection, params: LedgerQuery): string {
  const qs = new URLSearchParams();
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.q) qs.set("q", params.q);
  if (params.counterparty) qs.set(direction === "inbound" ? "supplier" : "buyer", params.counterparty);
  if (params.kind) qs.set("kind", params.kind);
  if (params.view) qs.set("view", params.view);
  if (params.page) qs.set("page", String(params.page));
  return qs.toString();
}

export async function fetchLedger(
  slug: string,
  direction: LedgerDirection,
  params: LedgerQuery = {},
): Promise<LedgerPage> {
  const qs = ledgerQs(direction, params);
  return (await api.get(`/t/${slug}/${direction}${qs ? "?" + qs : ""}`)) as LedgerPage;
}

/** Everyone the ledger can be filtered by: suppliers who delivered, buyers invoiced. */
export async function fetchLedgerCounterparties(
  slug: string,
  direction: LedgerDirection,
): Promise<string[]> {
  const tail = direction === "inbound" ? "suppliers" : "buyers";
  return (await api.get(`/t/${slug}/${direction}/${tail}`)) as string[];
}

/** The file for the accountant — same filter, as a semicolon CSV. */
/** A booked delivery as an A4 delivery note — shown inline, or as a file. */
export function receiptPdfUrl(slug: string, id: number, download = false): string {
  return `/api/v1/t/${slug}/inbound/${id}/pdf${download ? "?download=true" : ""}`;
}

export function ledgerCsvUrl(
  slug: string,
  direction: LedgerDirection,
  params: LedgerQuery = {},
): string {
  const qs = ledgerQs(direction, { ...params, page: undefined });
  return `/api/v1/t/${slug}/${direction}.csv${qs ? "?" + qs : ""}`;
}

export type ReceiptLineIn = {
  product_id: number;
  quantity: string;
  /** A RECEIPT_UNITS code ($lib/invoice); pieces when left out. */
  unit?: string;
  unit_price?: string;
  vat_rate?: string;
  batch_no: string;
  manufactured_on: string | null;
  expires_on: string | null;
  /** «Место на складе» the goods were put in. */
  location?: string;
} & LineMarks;

export type ReceiptIn = {
  supplier_name: string;
  /** One of our partner suppliers, picked from the list: files the note under it. */
  supplier_slug?: string;
  number: string;
  /** The date printed on the note. */
  received_on: string;
  /** The day it is typed in; the server takes today when omitted. */
  entered_on?: string | null;
  /** When the note is to be paid — or `prepaid`, and then no date. */
  payment_due_on?: string | null;
  prepaid?: boolean;
  /** Bought across a border: no VAT on any line. */
  is_export?: boolean;
  marks?: ReceiptMark[];
  currency: string;
  note: string;
  lines: ReceiptLineIn[];
};

export type Receipt = LedgerRow & {
  /** The name as typed on the note — `counterparty` may be our books' name. */
  supplier_name?: string;
  note: string;
  items: LedgerLine[];
};

/** Type in a delivery note from a supplier who is not on the platform. The
 *  goods land on the shelf as it is saved. */
export async function createReceipt(slug: string, payload: ReceiptIn): Promise<Receipt> {
  return (await api.post(`/t/${slug}/inbound`, payload)) as Receipt;
}

/** A name the note's supplier box offers. `slug` is set for one of our partner
 *  suppliers — those come first — and empty for a name typed somewhere before. */
export type ReceiptSupplierOption = { name: string; slug: string };

export async function fetchReceiptSupplierOptions(slug: string): Promise<ReceiptSupplierOption[]> {
  return (await api.get(`/t/${slug}/inbound/supplier-options`)) as ReceiptSupplierOption[];
}

/** Correct a hand-typed note — any field, any line. Stock moves by the difference. */
export async function updateReceipt(slug: string, id: number, payload: ReceiptIn): Promise<Receipt> {
  return (await api.put(`/t/${slug}/inbound/${id}`, payload)) as Receipt;
}

export async function fetchReceipt(slug: string, id: number): Promise<Receipt> {
  return (await api.get(`/t/${slug}/inbound/${id}`)) as Receipt;
}

/** Only a hand-typed receipt; one the pipeline wrote is corrected on its order. */
export async function deleteReceipt(slug: string, id: number): Promise<void> {
  await api.delete(`/t/${slug}/inbound/${id}`);
}

export async function reverseReceipt(slug: string, id: number, reason: string): Promise<void> {
  await api.post(`/t/${slug}/inbound/${id}/reverse`, { reason });
}
